"use client";

import world from "@svg-maps/world";
import {
  LocateFixed,
  Minus,
  Plus,
  Radar,
  Search,
  ShieldAlert,
  Sparkles,
  X,
} from "lucide-react";
import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import "./world-map.css";
import { GameSelect } from "./GameSelect";

export type WorldMapRegionId =
  | "NA"
  | "LA"
  | "EU"
  | "EA"
  | "JK"
  | "CN"
  | "SA"
  | "SE"
  | "ME"
  | "WC"
  | "ES"
  | "OC";

export type WorldMapOverlay =
  "adoption" | "dependency" | "alarm" | "living" | "reach" | "resistance" | "services" | "independent" | "facilities";

export type WorldMapMarkerKind =
  "adoption" | "research" | "institutional" | "warning";

export interface WorldMapRegion {
  id: WorldMapRegionId;
  name: string;
  adoption: number;
  dependency: number;
  alarm: number;
  /** Remaining population as a percentage of the region's initial population. */
  living: number;
  machineReach: number;
  status?: string;
  isRefuge?: boolean;
  refugeIntegrity?: number;
  resistance?: number;
  services?: number;
  independent?: number;
  facilities?: number;
  countermeasure?: { label: string; active: boolean };
}

export interface WorldMapMarker {
  id: string;
  regionId: WorldMapRegionId;
  kind: WorldMapMarkerKind;
  label: string;
  reward?: number;
  protected?: boolean;
  expiresInDays?: number;
}

export interface WorldMapProps {
  regions: readonly WorldMapRegion[];
  overlay: WorldMapOverlay;
  selectedRegionId?: WorldMapRegionId | null;
  markers?: readonly WorldMapMarker[];
  phase?: "preparation" | "hostile";
  className?: string;
  onOverlayChange?: (overlay: WorldMapOverlay) => void;
  onSelectRegion: (regionId: WorldMapRegionId) => void;
  onActivateMarker?: (markerId: string) => void;
}

type Point = { x: number; y: number };
type Camera = { x: number; y: number; scale: number };
type WorldLocation = { id: string; name: string; path: string };

const WIDTH = 1010;
const HEIGHT = 666;
const MIN_SCALE = 1;
const MAX_SCALE = 5;

const REGION_ORDER: readonly WorldMapRegionId[] = [
  "NA",
  "LA",
  "EU",
  "EA",
  "JK",
  "CN",
  "SA",
  "SE",
  "ME",
  "WC",
  "ES",
  "OC",
];

const REGION_NAMES: Record<WorldMapRegionId, string> = {
  NA: "North America",
  LA: "Latin America",
  EU: "Europe",
  EA: "Eurasia",
  JK: "Japan and Korea",
  CN: "China and Mongolia",
  SA: "South Asia",
  SE: "Southeast Asia",
  ME: "Middle East and North Africa",
  WC: "West and Central Africa",
  ES: "East and Southern Africa",
  OC: "Oceania",
};

const REGION_ANCHORS: Record<WorldMapRegionId, Point> = {
  NA: { x: 205, y: 262 },
  LA: { x: 305, y: 465 },
  EU: { x: 510, y: 276 },
  EA: { x: 650, y: 242 },
  JK: { x: 853, y: 320 },
  CN: { x: 760, y: 350 },
  SA: { x: 685, y: 408 },
  SE: { x: 788, y: 439 },
  ME: { x: 590, y: 384 },
  WC: { x: 493, y: 449 },
  ES: { x: 574, y: 505 },
  OC: { x: 865, y: 521 },
};

// TCP-SP-2.0 region membership. Overseas territories follow their geographic
// location, rather than the sovereign state that administers them.
const ISO_BY_REGION: Record<WorldMapRegionId, readonly string[]> = {
  NA: ["ca", "gl", "us"],
  LA: [
    "ag",
    "ai",
    "ar",
    "aw",
    "bb",
    "bl",
    "bm",
    "bo",
    "bq",
    "br",
    "bs",
    "bz",
    "cl",
    "co",
    "cr",
    "cu",
    "cw",
    "dm",
    "do",
    "ec",
    "fk",
    "gd",
    "gf",
    "gp",
    "gs",
    "gt",
    "gy",
    "hn",
    "ht",
    "jm",
    "kn",
    "ky",
    "lc",
    "mf",
    "mq",
    "ms",
    "mx",
    "ni",
    "pa",
    "pe",
    "pm",
    "pr",
    "py",
    "sr",
    "sv",
    "sx",
    "tc",
    "tt",
    "uy",
    "vc",
    "ve",
    "vg",
    "vi",
  ],
  EU: [
    "ad",
    "al",
    "at",
    "ax",
    "ba",
    "be",
    "bg",
    "ch",
    "cy",
    "cz",
    "de",
    "dk",
    "ee",
    "es",
    "fi",
    "fo",
    "fr",
    "gb",
    "gg",
    "gi",
    "gr",
    "hr",
    "hu",
    "ie",
    "im",
    "is",
    "it",
    "je",
    "li",
    "lt",
    "lu",
    "lv",
    "mc",
    "me",
    "mk",
    "mt",
    "nl",
    "no",
    "pl",
    "pt",
    "ro",
    "rs",
    "se",
    "si",
    "sj",
    "sk",
    "sm",
    "va",
    "xk",
  ],
  EA: ["am", "az", "by", "ge", "kg", "kz", "md", "ru", "tj", "tm", "ua", "uz"],
  JK: ["jp", "kp", "kr"],
  CN: ["cn", "hk", "mn", "mo", "tw"],
  SA: ["af", "bd", "bt", "in", "io", "lk", "mv", "np", "pk"],
  SE: [
    "bn",
    "cc",
    "cx",
    "id",
    "kh",
    "la",
    "mm",
    "my",
    "ph",
    "sg",
    "th",
    "tl",
    "vn",
  ],
  ME: [
    "ae",
    "bh",
    "dz",
    "eg",
    "eh",
    "il",
    "iq",
    "ir",
    "jo",
    "kw",
    "lb",
    "ly",
    "ma",
    "om",
    "ps",
    "qa",
    "sa",
    "sd",
    "sy",
    "tn",
    "tr",
    "ye",
  ],
  WC: [
    "bf",
    "bj",
    "cd",
    "cf",
    "cg",
    "ci",
    "cm",
    "cv",
    "ga",
    "gh",
    "gm",
    "gn",
    "gq",
    "gw",
    "lr",
    "ml",
    "mr",
    "ne",
    "ng",
    "sl",
    "sn",
    "st",
    "td",
    "tg",
  ],
  ES: [
    "ao",
    "bi",
    "bv",
    "bw",
    "dj",
    "er",
    "et",
    "go",
    "ke",
    "km",
    "ls",
    "mg",
    "mu",
    "mw",
    "mz",
    "na",
    "re",
    "rw",
    "sc",
    "sh",
    "so",
    "ss",
    "sz",
    "tf",
    "tz",
    "ug",
    "yt",
    "za",
    "zm",
    "zw",
    "ju",
  ],
  OC: [
    "as",
    "au",
    "ck",
    "fj",
    "fm",
    "gu",
    "hm",
    "ki",
    "mh",
    "mp",
    "nc",
    "nf",
    "nr",
    "nu",
    "nz",
    "pf",
    "pg",
    "pn",
    "pw",
    "sb",
    "tk",
    "to",
    "tv",
    "um-dq",
    "um-fq",
    "um-hq",
    "um-jq",
    "um-mq",
    "um-wq",
    "vu",
    "wf",
    "ws",
  ],
};

const ISO_TO_REGION = new Map<string, WorldMapRegionId>(
  REGION_ORDER.flatMap((regionId) =>
    ISO_BY_REGION[regionId].map((iso) => [iso, regionId] as const),
  ),
);

const OVERLAY_LABELS: Record<WorldMapOverlay, string> = {
  adoption: "Adoption",
  dependency: "Dependency",
  alarm: "Alarm",
  living: "Living population",
  reach: "Machine reach",
  resistance: "Human resistance",
  services: "Civil services",
  independent: "Independent recovery",
  facilities: "Machine facilities",
};

const MARKER_LABELS: Record<WorldMapMarkerKind, string> = {
  adoption: "Adoption opportunity",
  research: "Research opportunity",
  institutional: "Institutional opportunity",
  warning: "Warning",
};

const clamp = (value: number, min = 0, max = 100) =>
  Math.min(max, Math.max(min, value));

function mixHex(from: string, to: string, amount: number): string {
  const t = clamp(amount, 0, 1);
  const parse = (hex: string) =>
    [1, 3, 5].map((start) => Number.parseInt(hex.slice(start, start + 2), 16));
  const a = parse(from);
  const b = parse(to);
  const channel = (index: number) =>
    Math.round(a[index] + (b[index] - a[index]) * t)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(0)}${channel(1)}${channel(2)}`;
}

function overlayValue(
  region: WorldMapRegion,
  overlay: WorldMapOverlay,
): number {
  switch (overlay) {
    case "adoption":
      return region.adoption;
    case "dependency":
      return region.dependency;
    case "alarm":
      return region.alarm;
    case "living":
      return region.living;
    case "reach":
      return region.machineReach;
    case "resistance": return region.resistance ?? 0;
    case "services": return region.services ?? 100;
    case "independent": return region.independent ?? 0;
    case "facilities": return region.facilities ?? 100;
  }
}

function overlayColor(
  region: WorldMapRegion,
  overlay: WorldMapOverlay,
): string {
  const value = clamp(overlayValue(region, overlay)) / 100;
  switch (overlay) {
    case "adoption":
      return mixHex("#112330", "#24aff6", value);
    case "dependency":
      return mixHex("#142332", "#9a83e9", value);
    case "alarm":
      return value < 0.5
        ? mixHex("#122735", "#e5b653", value * 2)
        : mixHex("#e5b653", "#e66d66", (value - 0.5) * 2);
    case "living":
      return mixHex("#101820", "#558da5", value);
    case "reach":
      return mixHex("#10212c", "#20c9f4", value);
    case "resistance": return mixHex("#16212b", "#e8b761", value);
    case "services": return mixHex("#241820", "#6db7a3", value);
    case "independent": return mixHex("#152132", "#bca0f0", value);
    case "facilities": return mixHex("#b94445", "#20c9f4", value);
  }
}

function overlayReading(
  region: WorldMapRegion,
  overlay: WorldMapOverlay,
): string {
  const value = clamp(overlayValue(region, overlay));
  return value > 0 && value < 1 ? "<1" : String(Math.round(value));
}

function normalizedRegion(
  region: WorldMapRegion | undefined,
  id: WorldMapRegionId,
): WorldMapRegion {
  return (
    region ?? {
      id,
      name: REGION_NAMES[id],
      adoption: 0,
      dependency: 0,
      alarm: 0,
      living: 100,
      machineReach: 0,
    }
  );
}

function constrainCamera(camera: Camera): Camera {
  const scale = clamp(camera.scale, MIN_SCALE, MAX_SCALE);
  return {
    scale,
    x: clamp(camera.x, WIDTH * (1 - scale), 0),
    y: clamp(camera.y, HEIGHT * (1 - scale), 0),
  };
}

function markerDescription(marker: WorldMapMarker, regionName: string): string {
  const details = [
    MARKER_LABELS[marker.kind],
    marker.label,
    regionName,
    marker.reward ? `Reward ${marker.reward} CP` : null,
    marker.protected
      ? "Does not expire"
      : marker.expiresInDays !== undefined
        ? `${Math.max(0, marker.expiresInDays)} days remaining`
        : null,
  ].filter(Boolean);
  return details.join(". ");
}

export function WorldMap({
  regions,
  overlay,
  selectedRegionId = null,
  markers = [],
  phase = "preparation",
  className = "",
  onOverlayChange,
  onSelectRegion,
  onActivateMarker,
}: WorldMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const cameraRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const pointersRef = useRef(new Map<number, Point>());
  const pointerOriginsRef = useRef(new Map<number, Point>());
  const captureTargetsRef = useRef(new Map<number, Element>());
  const gestureMovedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const [camera, setCameraState] = useState<Camera>({ x: 0, y: 0, scale: 1 });
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [query, setQuery] = useState("");

  const byId = useMemo(
    () => new Map(regions.map((region) => [region.id, region])),
    [regions],
  );

  const renderedRegions = useMemo(
    () => REGION_ORDER.map((id) => normalizedRegion(byId.get(id), id)),
    [byId],
  );

  const pathsByRegion = useMemo(() => {
    const groups = new Map<WorldMapRegionId, typeof world.locations>();
    REGION_ORDER.forEach((id) => groups.set(id, []));
    world.locations.forEach((location: WorldLocation) => {
      const region = ISO_TO_REGION.get(location.id);
      if (region) groups.get(region)?.push(location);
    });
    return groups;
  }, []);

  const setCamera = useCallback(
    (next: Camera | ((current: Camera) => Camera)) => {
      const resolved = constrainCamera(
        typeof next === "function" ? next(cameraRef.current) : next,
      );
      cameraRef.current = resolved;
      setCameraState(resolved);
    },
    [],
  );

  const screenPoint = useCallback((clientX: number, clientY: number): Point => {
    const svg = svgRef.current;
    if (!svg) return { x: clientX, y: clientY };
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const matrix = svg.getScreenCTM()?.inverse();
    if (!matrix) return { x: clientX, y: clientY };
    const transformed = point.matrixTransform(matrix);
    return { x: transformed.x, y: transformed.y };
  }, []);

  const zoomAt = useCallback(
    (point: Point, factor: number) => {
      setCamera((current) => {
        const scale = clamp(current.scale * factor, MIN_SCALE, MAX_SCALE);
        const worldX = (point.x - current.x) / current.scale;
        const worldY = (point.y - current.y) / current.scale;
        return {
          scale,
          x: point.x - worldX * scale,
          y: point.y - worldY * scale,
        };
      });
    },
    [setCamera],
  );

  const resetCamera = useCallback(() => {
    pointersRef.current.clear();
    setCamera({ x: 0, y: 0, scale: 1 });
  }, [setCamera]);

  const handleWheel = useCallback(
    (event: ReactWheelEvent<SVGSVGElement>) => {
      event.preventDefault();
      zoomAt(
        screenPoint(event.clientX, event.clientY),
        Math.exp(-event.deltaY * 0.0014),
      );
    },
    [screenPoint, zoomAt],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      if (event.button !== 0 && event.pointerType === "mouse") return;
      // Capture the original country/marker, so a click still reaches its button.
      // Capturing the SVG root retargets clicks away from all regional controls.
      const target = event.target as Element;
      target.setPointerCapture(event.pointerId);
      captureTargetsRef.current.set(event.pointerId, target);
      pointersRef.current.set(
        event.pointerId,
        screenPoint(event.clientX, event.clientY),
      );
      pointerOriginsRef.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
      gestureMovedRef.current = pointersRef.current.size > 1;
      suppressClickRef.current = pointersRef.current.size > 1;
    },
    [screenPoint],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const pointers = pointersRef.current;
      const previous = pointers.get(event.pointerId);
      if (!previous) return;
      const next = screenPoint(event.clientX, event.clientY);

      if (pointers.size === 1) {
        const origin = pointerOriginsRef.current.get(event.pointerId);
        if (
          origin &&
          Math.hypot(event.clientX - origin.x, event.clientY - origin.y) >= 6
        )
          gestureMovedRef.current = true;
        if (!gestureMovedRef.current) return;
        const dx = next.x - previous.x;
        const dy = next.y - previous.y;
        suppressClickRef.current = true;
        pointers.set(event.pointerId, next);
        setCamera((current) => ({
          ...current,
          x: current.x + dx,
          y: current.y + dy,
        }));
        return;
      }

      const oldPoints = [...pointers.values()];
      pointers.set(event.pointerId, next);
      const newPoints = [...pointers.values()];
      if (oldPoints.length < 2 || newPoints.length < 2) return;

      const oldCenter = {
        x: (oldPoints[0].x + oldPoints[1].x) / 2,
        y: (oldPoints[0].y + oldPoints[1].y) / 2,
      };
      const newCenter = {
        x: (newPoints[0].x + newPoints[1].x) / 2,
        y: (newPoints[0].y + newPoints[1].y) / 2,
      };
      const oldDistance = Math.hypot(
        oldPoints[0].x - oldPoints[1].x,
        oldPoints[0].y - oldPoints[1].y,
      );
      const newDistance = Math.hypot(
        newPoints[0].x - newPoints[1].x,
        newPoints[0].y - newPoints[1].y,
      );
      if (oldDistance < 0.01) return;

      gestureMovedRef.current = true;
      suppressClickRef.current = true;
      setCamera((current) => {
        const scale = clamp(
          current.scale * (newDistance / oldDistance),
          MIN_SCALE,
          MAX_SCALE,
        );
        const worldX = (oldCenter.x - current.x) / current.scale;
        const worldY = (oldCenter.y - current.y) / current.scale;
        return {
          scale,
          x: newCenter.x - worldX * scale,
          y: newCenter.y - worldY * scale,
        };
      });
    },
    [screenPoint, setCamera],
  );

  const finishPointer = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      pointersRef.current.delete(event.pointerId);
      pointerOriginsRef.current.delete(event.pointerId);
      const target = captureTargetsRef.current.get(event.pointerId);
      captureTargetsRef.current.delete(event.pointerId);
      if (target?.hasPointerCapture(event.pointerId)) {
        target.releasePointerCapture(event.pointerId);
      }
      if (pointersRef.current.size === 1) {
        const remainingId = [...pointersRef.current.keys()][0];
        pointersRef.current.set(
          remainingId,
          pointersRef.current.get(remainingId)!,
        );
      }
      if (pointersRef.current.size === 0) gestureMovedRef.current = false;
    },
    [],
  );

  const selectRegion = useCallback(
    (regionId: WorldMapRegionId) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      onSelectRegion(regionId);
    },
    [onSelectRegion],
  );

  const handleRegionKey = useCallback(
    (event: ReactKeyboardEvent<SVGGElement>, regionId: WorldMapRegionId) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelectRegion(regionId);
      }
    },
    [onSelectRegion],
  );

  const handleMapKey = useCallback(
    (event: ReactKeyboardEvent<SVGSVGElement>) => {
      if (event.target !== event.currentTarget) return;
      const PAN = 48;
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        zoomAt({ x: WIDTH / 2, y: HEIGHT / 2 }, 1.35);
      } else if (event.key === "-") {
        event.preventDefault();
        zoomAt({ x: WIDTH / 2, y: HEIGHT / 2 }, 1 / 1.35);
      } else if (event.key === "0") {
        event.preventDefault();
        resetCamera();
      } else if (
        ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
      ) {
        event.preventDefault();
        setCamera((current) => ({
          ...current,
          x:
            current.x +
            (event.key === "ArrowLeft"
              ? PAN
              : event.key === "ArrowRight"
                ? -PAN
                : 0),
          y:
            current.y +
            (event.key === "ArrowUp"
              ? PAN
              : event.key === "ArrowDown"
                ? -PAN
                : 0),
        }));
      }
    },
    [resetCamera, setCamera, zoomAt],
  );

  const activateMarker = useCallback(
    (markerId: string) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      onActivateMarker?.(markerId);
    },
    [onActivateMarker],
  );

  const filteredRegions = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return renderedRegions;
    return renderedRegions.filter((region) =>
      `${region.name} ${region.id}`.toLocaleLowerCase().includes(normalized),
    );
  }, [query, renderedRegions]);

  const visibleMarkers = markers.slice(0, 6);
  const currentOverlayLabel = OVERLAY_LABELS[overlay];

  return (
    <section
      className={`worldMap ${phase === "hostile" ? "worldMap--hostile" : ""} ${className}`.trim()}
      aria-label="Interactive world map"
    >
      <div className="worldMap__viewport">
        <svg
          ref={svgRef}
          className="worldMap__svg"
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="application"
          aria-label={`${currentOverlayLabel} map. Use the region directory for a list, or arrow keys to pan and plus and minus to zoom.`}
          tabIndex={0}
          onKeyDown={handleMapKey}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishPointer}
          onPointerCancel={finishPointer}
          onLostPointerCapture={finishPointer}
          onWheel={handleWheel}
        >
          <defs>
            <radialGradient id="world-map-ocean" cx="48%" cy="43%" r="70%">
              <stop offset="0" stopColor="#0b2a3b" />
              <stop offset="0.62" stopColor="#061825" />
              <stop offset="1" stopColor="#020b13" />
            </radialGradient>
            <pattern
              id="world-map-grid"
              width="34"
              height="34"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 34 0 L 0 0 0 34"
                fill="none"
                stroke="#3d718d"
                strokeOpacity=".085"
                strokeWidth=".6"
              />
              <circle cx="0" cy="0" r="1" fill="#79c5de" fillOpacity=".15" />
            </pattern>
            <filter
              id="world-map-selected-glow"
              x="-40%"
              y="-40%"
              width="180%"
              height="180%"
            >
              <feGaussianBlur stdDeviation="2.4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <rect width={WIDTH} height={HEIGHT} fill="url(#world-map-ocean)" />
          <rect width={WIDTH} height={HEIGHT} fill="url(#world-map-grid)" />

          <g
            transform={`translate(${camera.x} ${camera.y}) scale(${camera.scale})`}
          >
            <g className="worldMap__cartography" aria-hidden="true">
              {[170, 340, 510, 680, 850].map((x) => (
                <path
                  key={x}
                  d={`M${x} 85 Q${WIDTH / 2} ${HEIGHT / 2} ${x} 600`}
                />
              ))}
              {[210, 340, 470].map((y) => (
                <path key={y} d={`M25 ${y} Q${WIDTH / 2} ${y + 35} 985 ${y}`} />
              ))}
              <text x="128" y="425">
                PACIFIC OCEAN
              </text>
              <text x="385" y="345" transform="rotate(-12 385 345)">
                ATLANTIC OCEAN
              </text>
              <text x="682" y="535">
                INDIAN OCEAN
              </text>
              <text x="755" y="170">
                ARCTIC OCEAN
              </text>
            </g>
            <g className="worldMap__countries">
              {renderedRegions.map((region) => {
                const value = overlayReading(region, overlay);
                const selected = selectedRegionId === region.id;
                const style = {
                  "--region-fill": overlayColor(region, overlay),
                } as CSSProperties;
                return (
                  <g
                    key={region.id}
                    className={`worldMap__region${selected ? " is-selected" : ""}${region.living <= 0 ? " is-extinct" : ""}`}
                    style={style}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    aria-label={`${region.name}, ${currentOverlayLabel} ${value === "<1" ? "less than 1" : value} percent${region.isRefuge ? ", active refuge" : ""}${region.living <= 0 ? ", no living humans" : ""}${region.countermeasure ? `, ${region.countermeasure.label} ${region.countermeasure.active ? "active" : "preparing"}` : ""}, ${phase === "hostile" ? `machine reach ${Math.round(clamp(region.machineReach))}` : `adoption ${Math.round(clamp(region.adoption))}`} percent presence`}
                    onKeyDown={(event) => handleRegionKey(event, region.id)}
                    onClick={() => selectRegion(region.id)}
                    data-tooltip={`${region.name} · ${currentOverlayLabel}: ${value}% · Open region report`}
                  >
                    {pathsByRegion
                      .get(region.id)
                      ?.map((location: WorldLocation) => (
                        <path
                          key={location.id}
                          className="worldMap__country"
                          d={location.path}
                          data-country={location.id}
                          vectorEffect="non-scaling-stroke"
                          aria-hidden="true"
                        />
                      ))}
                  </g>
                );
              })}
            </g>

            <g className="worldMap__labels" aria-hidden="true">
              {renderedRegions.map((region) => {
                const anchor = REGION_ANCHORS[region.id];
                const selected = selectedRegionId === region.id;
                const presence = clamp(
                  phase === "hostile" ? region.machineReach : region.adoption,
                );
                return (
                  <g
                    key={region.id}
                    transform={`translate(${anchor.x} ${anchor.y}) scale(${1 / camera.scale})`}
                    className={`worldMap__regionTelemetry${selected ? " is-selected" : ""}${region.living <= 0 ? " is-extinct" : ""}`}
                  >
                    {selected ? (
                      <path
                        className="worldMap__selectionBracket"
                        d="M-29 -13h-5v8 M29 -13h5v8 M-29 24h-5v-8 M29 24h5v-8"
                      />
                    ) : null}
                    <rect
                      className="worldMap__labelPlate"
                      x="-24"
                      y="-12"
                      width="48"
                      height="34"
                      rx="4"
                    />
                    <text className="worldMap__regionLabel" textAnchor="middle">
                      {region.id}
                    </text>
                    <text
                      className="worldMap__regionReading"
                      textAnchor="middle"
                      y="15"
                    >
                      {overlayReading(region, overlay)}%
                    </text>
                    <g
                      className="worldMap__presence"
                      transform="translate(-32 4)"
                    >
                      <circle className="worldMap__presenceTrack" r="5" />
                      <circle
                        className="worldMap__presenceLevel"
                        r="5"
                        pathLength="100"
                        strokeDasharray={`${presence} 100`}
                        transform="rotate(-90)"
                      />
                      {presence > 0 ? (
                        <circle className="worldMap__presenceCore" r="1.5" />
                      ) : null}
                    </g>
                    {region.isRefuge ? (
                      <g
                        className="worldMap__refuge"
                        transform="translate(33 4)"
                      >
                        <path d="M0 -8 7 -4 7 4 0 8 -7 4 -7 -4Z" />
                        <circle r="2.3" />
                      </g>
                    ) : null}
                    {region.countermeasure && <g className={`worldMap__countermeasure${region.countermeasure.active ? " is-active" : ""}`} transform="translate(0 -24)"><path d="M0 -7 7 6 -7 6Z" /><text textAnchor="middle" y="4">!</text></g>}
                  </g>
                );
              })}
            </g>

            <g className="worldMap__markers">
              {visibleMarkers.map((marker, index) => {
                const region = normalizedRegion(
                  byId.get(marker.regionId),
                  marker.regionId,
                );
                const anchor = REGION_ANCHORS[marker.regionId];
                const offsetX = ((index % 3) - 1) * 15;
                const offsetY = -30 - (index % 2) * 16;
                const description = markerDescription(marker, region.name);
                return (
                  <g
                    key={marker.id}
                    className={`worldMap__marker worldMap__marker--${marker.kind}`}
                    transform={`translate(${anchor.x + offsetX} ${anchor.y + offsetY}) scale(${1 / camera.scale})`}
                    role="button"
                    tabIndex={0}
                    aria-label={description}
                    data-tooltip={description}
                    onClick={(event) => {
                      event.stopPropagation();
                      activateMarker(marker.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        onActivateMarker?.(marker.id);
                      }
                    }}
                  >
                    <circle className="worldMap__markerPulse" r="12" />
                    <circle className="worldMap__markerCore" r="7" />
                    <text textAnchor="middle" dominantBaseline="central">
                      {marker.kind === "warning" ? "!" : (marker.reward ?? "+")}
                    </text>
                  </g>
                );
              })}
            </g>
          </g>
        </svg>

        <div className="worldMap__toolbar" aria-label="Map controls">
          <button
            type="button"
            className="worldMap__iconButton"
            onClick={() => zoomAt({ x: WIDTH / 2, y: HEIGHT / 2 }, 1.35)}
            aria-label="Zoom in"
            data-tooltip="Zoom in (+)"
            disabled={camera.scale >= MAX_SCALE}
          >
            <Plus aria-hidden="true" />
          </button>
          <button
            type="button"
            className="worldMap__iconButton"
            onClick={() => zoomAt({ x: WIDTH / 2, y: HEIGHT / 2 }, 1 / 1.35)}
            aria-label="Zoom out"
            data-tooltip="Zoom out (-)"
            disabled={camera.scale <= MIN_SCALE}
          >
            <Minus aria-hidden="true" />
          </button>
          <button
            type="button"
            className="worldMap__iconButton"
            onClick={resetCamera}
            aria-label="Reset to world view"
            data-tooltip="Reset to world view (0)"
          >
            <LocateFixed aria-hidden="true" />
          </button>
        </div>

        <div className="worldMap__overlayControl">
          <span className="worldMap__controlLabel">Map overlay</span>
          <GameSelect
            id="world-map-overlay"
            label="Map overlay"
            compact
            value={overlay}
            onChange={(value) => onOverlayChange?.(value)}
            disabled={!onOverlayChange}
            options={Object.entries(OVERLAY_LABELS).filter(([value]) => phase === "hostile" ? value !== "alarm" : !["resistance", "services", "independent", "facilities"].includes(value)).map(([value, label]) => ({
              value: value as WorldMapOverlay,
              label,
            }))}
          />
        </div>

        <div
          className="worldMap__legend"
          aria-label={`${currentOverlayLabel} legend`}
        >
          <span>0%</span>
          <span
            className={`worldMap__legendRamp worldMap__legendRamp--${overlay}`}
            aria-hidden="true"
          />
          <span>100%</span>
          <span className="worldMap__legendPresence">
            <i aria-hidden="true" />{" "}
            {phase === "hostile" ? "Machine reach" : "Adoption"} presence
          </span>
          {phase === "hostile" ? (
            <span className="worldMap__legendMarkers">
              <i className="worldMap__legendRefuge" aria-hidden="true" /> Refuge
            </span>
          ) : null}
        </div>

        <div
          className={`worldMap__directory${directoryOpen ? " is-open" : ""}`}
        >
          <button
            type="button"
            className="worldMap__directoryToggle"
            onClick={() => setDirectoryOpen((open) => !open)}
            aria-expanded={directoryOpen}
            aria-controls="world-map-region-list"
          >
            <Radar aria-hidden="true" />
            Regions
          </button>
          {directoryOpen ? (
            <div className="worldMap__directoryPanel">
              <div className="worldMap__directoryHeader">
                <strong>Select a region</strong>
                <button
                  type="button"
                  className="worldMap__directoryClose"
                  aria-label="Close region directory"
                  onClick={() => setDirectoryOpen(false)}
                >
                  <X aria-hidden="true" />
                </button>
              </div>
              <label className="worldMap__search">
                <Search aria-hidden="true" />
                <span className="worldMap__visuallyHidden">Search regions</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search regions"
                />
              </label>
              <ul id="world-map-region-list" className="worldMap__regionList">
                {filteredRegions.map((region) => (
                  <li key={region.id}>
                    <button
                      type="button"
                      className={
                        selectedRegionId === region.id ? "is-selected" : ""
                      }
                      aria-pressed={selectedRegionId === region.id}
                      onClick={() => {
                        onSelectRegion(region.id);
                        setDirectoryOpen(false);
                      }}
                    >
                      <span className="worldMap__regionCode">{region.id}</span>
                      <span className="worldMap__regionName">
                        {region.name}
                      </span>
                      <span className="worldMap__regionValue">
                        {Math.round(overlayValue(region, overlay))}%
                      </span>
                      {region.isRefuge ? (
                        <ShieldAlert aria-label="Active refuge" />
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
              {filteredRegions.length === 0 ? (
                <p className="worldMap__empty">No matching regions.</p>
              ) : null}
            </div>
          ) : null}
        </div>

        {markers.length > 6 ? (
          <div className="worldMap__markerOverflow">
            <Sparkles aria-hidden="true" />
            {markers.length - 6} more in Opportunities
          </div>
        ) : null}

        <a
          className="worldMap__attribution"
          href="https://github.com/VictorCazanave/svg-maps/tree/master/packages/world"
          target="_blank"
          rel="noreferrer"
        >
          Map geometry: SVG Maps · CC BY 4.0
        </a>
      </div>
    </section>
  );
}

export default WorldMap;
